'use client';

import { useLocale, useTranslations } from 'next-intl';
import React, { useMemo, useState } from 'react';

import { ShowingInfoModal } from '@/components/showing-details/ShowingInfoModal';
import TicketLink, { useTicketUrl } from '@/components/ticket-shape/TicketLink';

import { ShowingDto } from '@cineamo/api/models/showing/ShowingDto.types';

import { HrefOrHrefWithParams } from 'next-intl/dist/types/navigation/shared/utils';

import { getAgeRatingIcon } from '@/helpers/movie-helper';
import { isDatetimePast } from '@/helpers/time-helper';
import { cn } from '@cineamo/ui/src/helpers/cn';
import { useAgeRatingText } from '@cineamo/ui/src/helpers/movie-helper';
import {
    getShowingPropertiesDimension,
    getShowingPropertiesLanguage,
    getShowingTechnologies
} from '@cineamo/ui/src/helpers/showing-helper';

import { CachedContentData } from '@/hooks/useCachedContentData';
import { useFormat } from '@cineamo/ui/src/hooks/formatter/useFormat';

import { IconInfo16 } from '@cineamo/ui/src/icons/ic-info';
import { IconTicket2_24 } from '@cineamo/ui/src/icons/ic-ticket';
import ImageWithTransition from '@cineamo/ui/src/image/ImageWithTransition';

import { Link } from '@/i18n/routing';
import { useCinemaRooms } from '@/providers/CinemaRoomProvider';
import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';
import { useShowingTags } from '@/providers/ShowingTagProvider';

type ProgramCompactListItemProps = {
    showing: ShowingDto;
    content?: CachedContentData;
    showDate?: boolean;
    alignTicketButtonWithHighlights?: boolean;
    /**
     * Wide-screen proportions: a larger poster, a wider time column and a ticket button carrying its
     * label rather than the bare icon. Same structure, just not squeezed into a phone's width.
     */
    isDesktop?: boolean;
};

export function ProgramCompactListItem(props: ProgramCompactListItemProps): React.JSX.Element {
    const { showing, content, showDate, alignTicketButtonWithHighlights, isDesktop } = props;

    const t = useTranslations();
    const locale = useLocale();
    const domainConfiguration = useDomainConfiguration();
    const format = useFormat({ timeZone: domainConfiguration.timeZone });
    const { getCinemaRoomById } = useCinemaRooms();
    const { getShowingTagById } = useShowingTags();
    const { ticketUrl, hasTicketUrl, isExternal } = useTicketUrl(showing, content?.movieId);
    const isTicketClickDisabled = isDatetimePast(showing.startDatetime, true);

    const [isInfoModalOpen, setIsInfoModalOpen] = useState<boolean>(false);

    const time = format.dateTime.formatDate_HHmm24(showing.startDatetime);
    const date = showDate
        ? `${format.dateTime.formatWeekday(showing.startDatetime, 'short').replace('.', '')} ${format.dateTime.formatDayMonth(showing.startDatetime)}`
        : null;
    const cinemaRoom = getCinemaRoomById(showing.cinemaRoomId);
    const title = content?.title || showing.name;

    const href: HrefOrHrefWithParams<'/program/[contentId]'> | undefined = content
        ? {
              pathname: '/program/[contentId]',
              params: {
                  contentId: content.id
              }
          }
        : undefined;

    const showingTags = useMemo(() => {
        return showing.showingTagIds?.map((tagId) => getShowingTagById(tagId)).filter((tag) => !!tag) ?? [];
    }, [showing.showingTagIds, getShowingTagById]);

    const specialsLabel = showingTags
        .map((tag) => tag.name)
        .filter((label) => !!label)
        .join(', ');

    const infoLabels = useMemo(() => {
        const technologies = getShowingTechnologies(showing).map((technology) =>
            t(`showtimes.showing-info.technology.${technology}`)
        );
        return [
            // The shared helper returns null for a 2D showing (the web app hides it); this website
            // labels it explicitly.
            getShowingPropertiesDimension(showing) ?? '2D',
            ...technologies,
            ...getShowingPropertiesLanguage(showing, locale)
        ].filter((label) => !!label);
    }, [showing, locale, t]);

    const countryCode = domainConfiguration.cinema.countryCode;
    const ageRatingBadge = getAgeRatingIcon(content?.ageRating, countryCode, {
        movieTitle: title,
        movieId: content?.movieId,
        // Matches the height of the cinema room badge beside it (12px text + py-2), rather than the
        // icon's own 26px.
        classNameIcon: 'w-22 h-22'
    });
    const ageRatingText = useAgeRatingText(content?.ageRating, countryCode);
    // Only Germany has icons for its certifications, so elsewhere the rating takes the badge's place
    // as a label of the meta row - the same rule the shared `ContentTitle` follows, and what the
    // grouped compact list shows in its subtitle.
    const metaLabels = [ageRatingBadge ? undefined : ageRatingText, ...infoLabels].filter((label) => !!label);

    return (
        <div
            className={cn('flex flex-row', isDesktop ? 'gap-x-16 py-16' : 'gap-x-12 py-12')}
            role="group"
            aria-label={title}>
            <div className={cn('flex-none pt-2 flex flex-col', isDesktop ? 'w-64' : 'w-44')}>
                {date && (
                    <span className="text-body-bold-10 text-[rgba(var(--on-background-color-rgb),0.7)]">{date}</span>
                )}
                <span className="text-body-bold-14 tracking-[0.5px]">{time}</span>
            </div>
            {content && (
                <Link
                    className="flex-none pt-8"
                    href={href ?? ''}
                    aria-label={title}
                    prefetch={false}>
                    <ImageWithTransition
                        className="rounded-2 w-[44px] lg:w-[100px] aspect-[2/3]"
                        width={isDesktop ? 100 : 44}
                        height={isDesktop ? 130 : 66}
                        src={content.posterImage ?? ''}
                        alt={title}
                    />
                </Link>
            )}
            <div className="min-w-0 flex-1 flex flex-col gap-y-6">
                <div className="flex flex-row gap-x-12">
                    <div className="min-w-0 flex-1 flex flex-col gap-y-4">
                        {title &&
                            (href ? (
                                <Link
                                    href={href}
                                    prefetch={false}>
                                    <span className="text-body-bold-16 tracking-[0.8px] line-clamp-2 [word-break:break-word]">
                                        {title}
                                    </span>
                                </Link>
                            ) : (
                                <span className="text-body-bold-16 tracking-[0.8px] line-clamp-2 [word-break:break-word]">
                                    {title}
                                </span>
                            ))}
                        <div className="flex flex-row flex-wrap items-center gap-x-4 gap-y-2 py-4 text-body-12 text-[rgba(var(--on-background-color-rgb),0.7)]">
                            {ageRatingBadge && (
                                <span className="flex shrink-0 items-center pr-2">{ageRatingBadge}</span>
                            )}
                            {cinemaRoom && (
                                <span className="rounded-4 px-4 py-2 bg-[rgba(var(--on-background-color-rgb),0.1)] text-[var(--on-background-color)]">
                                    {cinemaRoom.name}
                                </span>
                            )}
                            {metaLabels.map((label, labelIndex) => (
                                <React.Fragment key={`ProgramCompactInfoLabel#${showing.id}-${labelIndex}`}>
                                    {(labelIndex > 0 || !!cinemaRoom) && (
                                        <span className="text-[rgba(var(--on-background-color-rgb),0.5)]">•</span>
                                    )}
                                    <span className="whitespace-pre">{label}</span>
                                </React.Fragment>
                            ))}
                            <button
                                type="button"
                                aria-label={t('showing-info.show-info')}
                                className="inline-flex cursor-pointer"
                                onClick={() => setIsInfoModalOpen(true)}>
                                <IconInfo16 className="w-14 h-14 opacity-60" />
                            </button>
                        </div>
                    </div>
                    <div
                        className={cn(
                            'flex-none pt-8',
                            // Align with the ticket button inside the highlight cards above (8px card inset)
                            alignTicketButtonWithHighlights && 'mr-8'
                        )}>
                        <TicketLink
                            showing={showing}
                            content={content}
                            className={cn('block h-40', isDesktop ? 'w-auto' : 'w-40')}>
                            <span
                                className={cn(
                                    'flex h-40 items-center justify-center gap-x-8 rounded-4 bg-[var(--primary-color)] text-[var(--on-primary-color)]',
                                    isDesktop ? 'px-16 text-body-bold-14' : 'w-40',
                                    isTicketClickDisabled
                                        ? 'opacity-60 cursor-not-allowed'
                                        : 'cursor-pointer clickable-element'
                                )}>
                                <IconTicket2_24 className="w-20 h-20 flex-none" />
                                {isDesktop && <span className="whitespace-nowrap">{t('program.tickets')}</span>}
                            </span>
                        </TicketLink>
                    </div>
                </div>
                {specialsLabel && <div className="text-body-bold-12 text-[var(--primary-color)]">{specialsLabel}</div>}
                {showing.highlightText && (
                    <div className="text-body-12 text-[rgba(var(--on-background-color-rgb),0.7)]">
                        {showing.highlightText}
                    </div>
                )}
            </div>
            {isInfoModalOpen && (
                <ShowingInfoModal
                    showing={showing}
                    content={content}
                    modalIsOpen={isInfoModalOpen}
                    setModalIsOpen={setIsInfoModalOpen}
                    specials={showingTags}
                    onClose={() => setIsInfoModalOpen(false)}
                    onShare={() => undefined}
                    onSubmit={() => {
                        if (hasTicketUrl) {
                            window.open(ticketUrl, isExternal ? '_blank' : '_self', 'noopener');
                        }
                    }}
                />
            )}
        </div>
    );
}
